import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { Badge } from '@shared/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { Textarea } from '@shared/components/ui/textarea';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { DraggableList } from '@shared/components/ui/draggable-list';
import { Plus, X, Pencil, AlertTriangle } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { toServiceStatusValue } from '@shared/lib/utils';

interface ServiceRequestStatus {
  id?: string;
  value: string;
  label: string;
  color?: string;
  isDefault?: boolean;
}

interface ServiceConfigStepProps {
  serviceTypes: any[];
  serviceCategories: any[];
  serviceRequestStatuses: string[];
  selectedTemplates: string[];
  templateOverrides: Record<string, any>;
  businessType: string;
  onServiceTypesChange: (value: any[]) => void;
  onServiceCategoriesChange: (value: any[]) => void;
  onServiceRequestStatusesChange: (value: any[]) => void;
  onSelectedTemplatesChange: (value: string[]) => void;
  onTemplateOverridesChange: (value: Record<string, any>) => void;
  onInvalidTemplatesChange: (value: boolean) => void;
}

const DEFAULT_SERVICE_REQUEST_STATUSES = [
  'Pending',
  'In Progress',
  'Completed',
  'Cancelled'
];

interface TemplateItem {
  id?: string;
  type: string;
  description: string;
  defaultQuantity: number;
  defaultUnitPrice: number;
  position?: number;
  partNumber?: string;
}

interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  defaultHours?: number;
  defaultRate?: number;
  items: TemplateItem[];
}

export default function ServiceConfigStep(props: ServiceConfigStepProps) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [defaults, setDefaults] = useState<any>(null);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAddPartDialogOpen, setIsAddPartDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<string | null>(null);
  const [deletedTemplates, setDeletedTemplates] = useState<Set<string>>(new Set());
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);
  const [newItem, setNewItem] = useState<TemplateItem>({ 
    type: 'parts', 
    description: '', 
    defaultQuantity: 1, 
    defaultUnitPrice: 0, 
    partNumber: '' 
  });
  const [loading, setLoading] = useState(false);
  const lastInvalidTemplatesRef = useRef<boolean | null>(null);

  // Input states for adding new items
  const [newServiceType, setNewServiceType] = useState('');
  const [newServiceCategory, setNewServiceCategory] = useState('');
  
  // State for managing status objects
  const [statusObjects, setStatusObjects] = useState<ServiceRequestStatus[]>([]);

  // Load templates and defaults on mount
  useEffect(() => {
    async function loadData() {
      console.log('[ServiceConfigStep] Loading data with businessType:', props.businessType);
      if (!props.businessType) {
        console.log('[ServiceConfigStep] No business type provided, skipping data load');
        return;
      }
      
      setLoading(true);
      try {
        // Load defaults
        console.log('[ServiceConfigStep] Fetching defaults for business type:', props.businessType);
        const defaultsRes = await apiRequest('GET', `/api/v1/onboarding/defaults/${props.businessType}`);
        const defaultsData = await defaultsRes.json();
        console.log('[ServiceConfigStep] Defaults API response:', defaultsData);
        setDefaults(defaultsData);

        // Load templates
        const templatesRes = await apiRequest('GET', `/api/v1/onboarding/templates/${props.businessType}`);
        const templatesData = await templatesRes.json();
        console.log('Templates API response:', templatesData);
        console.log('Is array?', Array.isArray(templatesData));
        // Extract templates array from response object
        const templatesArray = templatesData.templates || templatesData;
        const processedTemplates = Array.isArray(templatesArray) 
          ? templatesArray.map((template: any) => ({
              ...template,
              items: template.items || [] // Ensure items array exists
            }))
          : [];
        setTemplates(processedTemplates);
      } catch (err) {
        console.error('Failed to load service config data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [props.businessType]);

  // Convert string array to status objects, preserving colors from defaults
  useEffect(() => {
    if (!defaults?.serviceRequestStatuses) {
      // If no defaults, create basic status objects using the status as the label
      const statusObjects = props.serviceRequestStatuses.map((status: any, index) => ({
        id: `status_${index}`,
        value: toServiceStatusValue(typeof status === 'string' ? status : status.label || status.value || ''),
        label: typeof status === 'string' ? status : status.label || status.value || '',
        color: '#6b7280',
        isDefault: false
      }));
      setStatusObjects(statusObjects);
      return;
    }

    const statusObjects = props.serviceRequestStatuses.map((status: any, index) => {
      // If status is already an object, use it directly
      if (typeof status === 'object' && status !== null) {
        return {
          id: status.id || `status_${index}`,
          value: status.value || toServiceStatusValue(status.label),
          label: status.label,
          color: status.color || '#6b7280',
          isDefault: status.isDefault || false
        };
      }
      
      // If status is a string, try to find the original status object from defaults
      const originalStatus = defaults.serviceRequestStatuses.find((item: any) => {
        if (typeof item === 'string') {
          return item === status;
        }
        // Check both label and value for matching
        return (item.label === status) || (item.value === status);
      });
      
      // Use the label from the original status if available, otherwise use the status as-is
      const displayLabel = (typeof originalStatus === 'object' && originalStatus?.label) || status;
      
      return {
        id: `status_${index}`,
        value: toServiceStatusValue(typeof status === 'string' ? status : status.label || status.value || ''),
        label: displayLabel,
        color: (typeof originalStatus === 'object' && originalStatus?.color) || '#6b7280',
        isDefault: (typeof originalStatus === 'object' && originalStatus?.isDefault) || false
      };
    });
    setStatusObjects(statusObjects);
  }, [props.serviceRequestStatuses, defaults]);

  // Apply defaults when they're loaded
  useEffect(() => {
    if (!defaults) return;
    
    // Only apply defaults if the current values are empty
    if (props.serviceTypes.length === 0 && defaults.serviceTypes) {
      // Use the full objects from defaults to preserve label and value
      const serviceTypes = Array.isArray(defaults.serviceTypes)
        ? defaults.serviceTypes.map((item: any, index: number) => ({
            id: `service_type_${Date.now()}_${index}`,
            label: typeof item === 'string' ? item : item.label,
            value: typeof item === 'string' ? item : item.value
          }))
        : [];
      props.onServiceTypesChange(serviceTypes);
    }
    if (props.serviceCategories.length === 0 && defaults.serviceCategories) {
      // Use the full objects from defaults to preserve label and value
      const serviceCategories = Array.isArray(defaults.serviceCategories)
        ? defaults.serviceCategories.map((item: any, index: number) => ({
            id: `service_category_${Date.now()}_${index}`,
            label: typeof item === 'string' ? item : item.label,
            value: typeof item === 'string' ? item : item.value
          }))
        : [];
      props.onServiceCategoriesChange(serviceCategories);
    }
    if (props.serviceRequestStatuses.length === 0 && defaults.serviceRequestStatuses) {
      // Create status objects with full properties to preserve colors
      const statusObjects = defaults.serviceRequestStatuses.map((item: any, index: number) => ({
        id: `status_${index}`,
        value: typeof item === 'string' ? toServiceStatusValue(item) : toServiceStatusValue(item.label || item.value || ''),
        label: typeof item === 'string' ? item : item.label,
        color: typeof item === 'object' && item.color ? item.color : '#6b7280',
        isDefault: typeof item === 'object' && item.isDefault ? item.isDefault : false
      }));
      
      // Pass the full objects to preserve colors and other properties
      props.onServiceRequestStatusesChange(statusObjects);
      setStatusObjects(statusObjects);
    }
  }, [defaults, props.serviceTypes.length, props.serviceCategories.length, props.serviceRequestStatuses.length]);

  // Check for invalid template categories - only when templates are loaded
  useEffect(() => {
    if (templates.length === 0) return;
    
    const invalidTemplateCategoryIds = props.selectedTemplates.filter((id: string) => {
      const t = templates.find((tpl) => tpl.id === id);
      return t ? !templateCategoryValid(t) : false;
    });
    const hasInvalidTemplates = invalidTemplateCategoryIds.length > 0;
    
    // Only call onInvalidTemplatesChange if the result has actually changed
    if (lastInvalidTemplatesRef.current !== hasInvalidTemplates) {
      lastInvalidTemplatesRef.current = hasInvalidTemplates;
      props.onInvalidTemplatesChange(hasInvalidTemplates);
    }
  }, [props.selectedTemplates, props.serviceCategories, templates.length, defaults]);

  const templateCategoryValid = (template: Template) => {
    // Check if the template's category exists in the current service categories
    if (props.serviceCategories.length > 0) {
      return props.serviceCategories.some(cat => {
        if (typeof cat === 'string') {
          return cat === template.category;
        }
        return cat.value === template.category || cat.label === template.category;
      });
    }
    
    // If no service categories are set yet, check against defaults
    if (defaults?.serviceCategories) {
      return defaults.serviceCategories.some((cat: any) => {
        if (typeof cat === 'string') {
          return cat === template.category;
        }
        return cat.value === template.category || cat.label === template.category;
      });
    }
    
    // If no defaults either, assume it's valid (will be validated later)
    return true;
  };

  const handleAddServiceType = () => {
    if (newServiceType.trim() && !props.serviceTypes.includes(newServiceType.trim())) {
      const newType = {
        id: `service_type_${Date.now()}`,
        label: newServiceType.trim(),
        value: newServiceType.trim()
      };
      props.onServiceTypesChange([...props.serviceTypes, newType]);
      setNewServiceType('');
    }
  };

  const handleRemoveServiceType = (type: string) => {
    props.onServiceTypesChange(props.serviceTypes.filter(t => {
      if (typeof t === 'string') {
        return t !== type;
      }
      return t.label !== type && t.value !== type;
    }));
  };

  const handleAddServiceCategory = () => {
    if (newServiceCategory.trim() && !props.serviceCategories.includes(newServiceCategory.trim())) {
      const newCategory = {
        id: `service_category_${Date.now()}`,
        label: newServiceCategory.trim(),
        value: newServiceCategory.trim()
      };
      props.onServiceCategoriesChange([...props.serviceCategories, newCategory]);
      setNewServiceCategory('');
    }
  };

  const handleRemoveServiceCategory = (category: string) => {
    props.onServiceCategoriesChange(props.serviceCategories.filter(c => {
      if (typeof c === 'string') {
        return c !== category;
      }
      return c.label !== category && c.value !== category;
    }));
  };


  const handleKeyPress = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  // Helper functions to get human-readable labels
  const getServiceTypeLabel = (value: string) => {
    if (!defaults?.serviceTypes) return value;
    const serviceType = defaults.serviceTypes.find((item: any) => 
      (typeof item === 'string' ? item : item.value) === value
    );
    return (typeof serviceType === 'object' && serviceType?.label) || value;
  };

  const getServiceCategoryLabel = (value: string) => {
    if (!defaults?.serviceCategories) return value;
    const serviceCategory = defaults.serviceCategories.find((item: any) => 
      (typeof item === 'string' ? item : item.value) === value
    );
    return (typeof serviceCategory === 'object' && serviceCategory?.label) || value;
  };

  // Helper function to get the display template (with overrides applied)
  const getDisplayTemplate = (template: Template) => {
    const override = props.templateOverrides[template.id];
    return override ? { ...template, ...override } : template;
  };

  // Handlers for DraggableList
  const handleStatusItemsChange = (items: ServiceRequestStatus[]) => {
    setStatusObjects(items);
    // Pass the full objects to preserve colors and other properties
    props.onServiceRequestStatusesChange(items);
  };

  const handleStatusItemChange = (index: number, item: ServiceRequestStatus) => {
    const updatedItems = [...statusObjects];
    updatedItems[index] = item;
    setStatusObjects(updatedItems);
    // Pass the full objects to preserve colors and other properties
    props.onServiceRequestStatusesChange(updatedItems);
  };

  const handleStatusItemDelete = (index: number) => {
    const updatedItems = statusObjects.filter((_, i) => i !== index);
    setStatusObjects(updatedItems);
    // Pass the full objects to preserve colors and other properties
    props.onServiceRequestStatusesChange(updatedItems);
  };

  const handleStatusItemAdd = () => {
    const newStatus: ServiceRequestStatus = {
      id: `status_${Date.now()}`,
      value: `custom_${Date.now()}`,
      label: '',
      color: '#6b7280',
      isDefault: false
    };
    const updatedItems = [...statusObjects, newStatus];
    setStatusObjects(updatedItems);
    // Pass the full objects to preserve colors and other properties
    props.onServiceRequestStatusesChange(updatedItems);
  };

  const handleDeleteTemplate = (templateId: string) => {
    setTemplateToDelete(templateId);
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteTemplate = () => {
    if (!templateToDelete) return;
    
    // Add to deleted templates set
    setDeletedTemplates(prev => new Set([...prev, templateToDelete]));
    
    // Remove from selected templates if it was selected
    props.onSelectedTemplatesChange(props.selectedTemplates.filter(id => id !== templateToDelete));
    // Remove from template overrides if it exists
    const updatedOverrides = { ...props.templateOverrides };
    delete updatedOverrides[templateToDelete];
    props.onTemplateOverridesChange(updatedOverrides);
    
    // Close dialog and reset state
    setIsDeleteDialogOpen(false);
    setTemplateToDelete(null);
  };

  // Future functionality - undelete template
  // const handleUndeleteTemplate = (templateId: string) => {
  //   // Remove from deleted templates set
  //   setDeletedTemplates(prev => {
  //     const newSet = new Set(prev);
  //     newSet.delete(templateId);
  //     return newSet;
  //   });
  // };

  const handleEditTemplate = (template: Template) => {
    const displayTemplate = getDisplayTemplate(template);
    setEditingTemplate(displayTemplate);
    setIsDialogOpen(true);
  };

  const handleSaveTemplateEdit = () => {
    if (!editingTemplate) return;
    
    if (editingTemplate.id === '') {
      // Creating a new template - generate a temporary ID
      const newTemplateId = `temp_${Date.now()}`;
      const newTemplate = { ...editingTemplate, id: newTemplateId };
      
      // Add to selected templates
      props.onSelectedTemplatesChange([...props.selectedTemplates, newTemplateId]);
      
      // Add to template overrides
      const updatedOverrides = {
        ...props.templateOverrides,
        [newTemplateId]: newTemplate
      };
      props.onTemplateOverridesChange(updatedOverrides);
    } else {
      // Updating existing template
      const updatedOverrides = {
        ...props.templateOverrides,
        [editingTemplate.id]: editingTemplate
      };
      props.onTemplateOverridesChange(updatedOverrides);
    }
    
    setIsDialogOpen(false);
    setEditingTemplate(null);
  };

  const saveItem = () => {
    if (!editingTemplate) return;
    
    const staged = { ...newItem } as TemplateItem;
    if (editingItemIndex !== null) {
      // Update existing item
      const updatedItems = [...editingTemplate.items];
      updatedItems[editingItemIndex] = { ...updatedItems[editingItemIndex], ...staged };
      setEditingTemplate(prev => prev ? { ...prev, items: updatedItems } : prev);
    } else {
      // Add new item
      const updatedItems = [...editingTemplate.items, { ...staged, position: editingTemplate.items.length + 1 }];
      setEditingTemplate(prev => prev ? { ...prev, items: updatedItems } : prev);
    }
    setIsAddPartDialogOpen(false);
    setEditingItemIndex(null);
    setNewItem({ type: 'parts', description: '', defaultQuantity: 1, defaultUnitPrice: 0, partNumber: '' });
  };

  const handleResetDefaults = () => {
    if (!defaults) return;
    
    // Extract values from objects if needed
    const serviceTypes = Array.isArray(defaults.serviceTypes) 
      ? defaults.serviceTypes.map((item: any) => typeof item === 'string' ? item : item.value || item.label)
      : [];
    const serviceCategories = Array.isArray(defaults.serviceCategories)
      ? defaults.serviceCategories.map((item: any) => typeof item === 'string' ? item : item.value || item.label)
      : [];
    
    // Reset service configuration with objects
    const serviceTypeObjects = serviceTypes.map((type: any, index: number) => ({
      id: `service_type_${Date.now()}_${index}`,
      label: typeof type === 'string' ? type : type.label,
      value: typeof type === 'string' ? type : type.value
    }));
    const serviceCategoryObjects = serviceCategories.map((category: any, index: number) => ({
      id: `service_category_${Date.now()}_${index}`,
      label: typeof category === 'string' ? category : category.label,
      value: typeof category === 'string' ? category : category.value
    }));
    
    props.onServiceTypesChange(serviceTypeObjects);
    props.onServiceCategoriesChange(serviceCategoryObjects);
    // Reset service request statuses with objects
    const statusObjects = DEFAULT_SERVICE_REQUEST_STATUSES.map((status, index) => ({
      id: `status_${Date.now()}_${index}`,
      value: toServiceStatusValue(status),
      label: status,
      color: '#6b7280',
      isDefault: true
    }));
    props.onServiceRequestStatusesChange(statusObjects);
    
    // Reset templates to default selection (all available templates)
    const defaultSelectedTemplates = Array.isArray(templates) ? templates.map(t => t.id) : [];
    props.onSelectedTemplatesChange(defaultSelectedTemplates);
    props.onTemplateOverridesChange({});
    
    // Clear deleted templates set to show all templates again
    setDeletedTemplates(new Set());
  };

  // Listen for reset event from parent component
  useEffect(() => {
    const handleResetEvent = () => {
      handleResetDefaults();
    };
    
    window.addEventListener('resetDefaults', handleResetEvent);
    return () => window.removeEventListener('resetDefaults', handleResetEvent);
  }, []);

  if (loading) {
    return <div className="p-8 text-center">Loading service configuration...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Service Types */}
      <Card>
        <CardHeader>
          <CardTitle>Service Types</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {props.serviceTypes.map((type) => {
              const typeValue = typeof type === 'string' ? type : type.value;
              const typeLabel = typeof type === 'string' ? getServiceTypeLabel(type) : type.label;
              return (
                <Badge key={typeValue} variant="secondary" className="flex items-center gap-1">
                  {typeLabel}
                  <button
                    onClick={() => handleRemoveServiceType(typeValue)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              );
            })}
          </div>
          <div className="flex gap-2">
            <Input
              value={newServiceType}
              onChange={(e) => setNewServiceType(e.target.value)}
              onKeyPress={(e) => handleKeyPress(e, handleAddServiceType)}
              placeholder="Enter service type name"
              className="flex-1"
            />
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleAddServiceType}
              disabled={!newServiceType.trim()}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Service Categories */}
      <Card>
        <CardHeader>
          <CardTitle>Service Categories</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {props.serviceCategories.map((category) => {
              const categoryValue = typeof category === 'string' ? category : category.value;
              const categoryLabel = typeof category === 'string' ? getServiceCategoryLabel(category) : category.label;
              return (
                <Badge key={categoryValue} variant="secondary" className="flex items-center gap-1">
                  {categoryLabel}
                  <button
                    onClick={() => handleRemoveServiceCategory(categoryValue)}
                    className="ml-1 hover:text-destructive"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              );
            })}
          </div>
          <div className="flex gap-2">
            <Input
              value={newServiceCategory}
              onChange={(e) => setNewServiceCategory(e.target.value)}
              onKeyPress={(e) => handleKeyPress(e, handleAddServiceCategory)}
              placeholder="Enter service category name"
              className="flex-1"
            />
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleAddServiceCategory}
              disabled={!newServiceCategory.trim()}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Service Request Statuses */}
      <Card>
        <CardHeader>
          <CardTitle>Service Request Statuses</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <DraggableList
              items={statusObjects}
              onItemsChange={handleStatusItemsChange}
              onItemChange={handleStatusItemChange}
              onItemDelete={handleStatusItemDelete}
              onItemAdd={handleStatusItemAdd}
              placeholder="Status Name, e.g. 'On Hold'"
              addButtonText="Add Status"
              valueGenerator={toServiceStatusValue}
              showColorPicker={true}
              canDeleteItem={(item) => !item.isDefault}
            />
          </div>
        </CardContent>
      </Card>

      {/* Template Library */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Service Templates</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setEditingTemplate({
                  id: '',
                  name: '',
                  description: '',
                  category: props.serviceCategories[0] || '',
                  defaultHours: 1,
                  defaultRate: 100,
                  items: []
                });
                setIsDialogOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-muted-foreground mb-4">
            Templates in this list will be added to your shop. You can modify service types, categories, statuses, and templates anytime in Shop Settings.
          </div>
          
          {(() => {
            // Combine API templates with created templates, filter out deleted ones
            const allTemplates = [
              ...(templates || []).filter(t => !deletedTemplates.has(t.id)),
              ...Object.values(props.templateOverrides).filter(t => t.id.startsWith('temp_') && !deletedTemplates.has(t.id))
            ];
            
            return allTemplates.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {allTemplates.map((template) => {
                  const displayTemplate = getDisplayTemplate(template);
                  return (
                    <div key={template.id} className="border border-border rounded-lg p-3 hover:bg-muted/50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm truncate">{displayTemplate.name}</h4>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{displayTemplate.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="secondary" className="text-xs">
                              {getServiceCategoryLabel(displayTemplate.category)}
                            </Badge>
                            {template.id.startsWith('temp_') && (
                              <Badge variant="outline" className="text-xs">
                                New
                              </Badge>
                            )}
                            {!templateCategoryValid(displayTemplate) && (
                              <Badge variant="destructive" className="text-xs">
                                <AlertTriangle className="h-2 w-2 mr-1" />
                                Invalid
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditTemplate(displayTemplate)}
                            className="h-6 w-6 p-0"
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteTemplate(template.id)}
                            className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm">No templates available for your business type.</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => {
                    setEditingTemplate({
                      id: '',
                      name: '',
                      description: '',
                      category: props.serviceCategories[0] || '',
                      defaultHours: 1,
                      defaultRate: 100,
                      items: []
                    });
                    setIsDialogOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Template
                </Button>
              </div>
            );
          })()}
        </CardContent>
      </Card>


      {/* Template Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col border border-border">
          <DialogHeader>
            <DialogTitle>Edit Template: {editingTemplate?.name}</DialogTitle>
            <DialogDescription>Update the details for this service template.</DialogDescription>
          </DialogHeader>

          <form className="flex-1 overflow-auto space-y-4 py-4" onSubmit={(e) => { e.preventDefault(); handleSaveTemplateEdit(); }}>
            {/* General Section */}
            <div className="rounded-lg border border-border bg-card p-4 space-y-4">
              <div>
                <Label>Name</Label>
                <Input 
                  value={editingTemplate?.name || ''} 
                  onChange={(e) => setEditingTemplate(prev => prev ? { ...prev, name: e.target.value } : prev)} 
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select 
                  value={editingTemplate?.category || ''} 
                  onValueChange={(v) => setEditingTemplate(prev => prev ? { ...prev, category: v } : prev)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {props.serviceCategories.map(cat => (
                      <SelectItem key={cat} value={cat}>{getServiceCategoryLabel(cat)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea 
                  value={editingTemplate?.description || ''} 
                  onChange={(e) => setEditingTemplate(prev => prev ? { ...prev, description: e.target.value } : prev)} 
                />
              </div>
            </div>

            {/* Pricing Section */}
            <div className="rounded-lg border border-border bg-card p-4 space-y-4">
              <h3 className="text-lg font-semibold mb-2">Pricing</h3>
              <div className="grid grid-cols-2 gap-4 items-end">
                <div>
                  <Label>Hours</Label>
                  <Input 
                    type="number" 
                    step="0.5" 
                    min="0.1" 
                    value={editingTemplate?.defaultHours ?? 0.5} 
                    onChange={(e) => setEditingTemplate(prev => prev ? { ...prev, defaultHours: parseFloat(e.target.value || '0.5') } : prev)} 
                  />
                </div>
                <div>
                  <Label>Hourly Rate ($)</Label>
                  <Input 
                    type="number" 
                    step="0.01" 
                    min="0" 
                    value={editingTemplate?.defaultRate ?? 150} 
                    onChange={(e) => setEditingTemplate(prev => prev ? { ...prev, defaultRate: parseFloat(e.target.value || '0') } : prev)} 
                  />
                </div>
              </div>
            </div>

            {/* Parts List Section */}
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Parts List</h3>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditingItemIndex(null);
                    setNewItem({ type: 'parts', description: '', defaultQuantity: 1, defaultUnitPrice: 0, partNumber: '' });
                    setIsAddPartDialogOpen(true);
                  }}
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Part
                </Button>
              </div>
              {editingTemplate?.items && editingTemplate.items.length > 0 ? (
                <div className="space-y-2">
                  {editingTemplate.items.map((item, index) => (
                    <div key={item.id || index} className="flex items-center justify-between p-2 rounded bg-muted/50">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{item.description}</p>
                        {item.partNumber && (
                          <p className="text-xs text-muted-foreground">#{item.partNumber}</p>
                        )}
                        <p className="text-sm text-muted-foreground">
                          Type: {item.type} • Qty: {item.defaultQuantity} • Unit: ${item.defaultUnitPrice?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => {
                            setNewItem(item);
                            setEditingItemIndex(index);
                            setIsAddPartDialogOpen(true);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => {
                            const updatedItems = editingTemplate.items.filter((_, i) => i !== index);
                            setEditingTemplate(prev => prev ? { ...prev, items: updatedItems } : prev);
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground text-center py-8">No parts added yet</div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Part Dialog */}
      <Dialog open={isAddPartDialogOpen} onOpenChange={setIsAddPartDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] flex flex-col border border-border">
          <DialogHeader>
            <DialogTitle>{editingItemIndex !== null ? 'Edit Item' : 'Add Item'}</DialogTitle>
            <DialogDescription>{editingItemIndex !== null ? 'Update the details for this item.' : 'Add a new item to the template.'}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto space-y-4 py-4">
            <div className="space-y-4">
              <div>
                <Label>Description</Label>
                <Input 
                  value={newItem.description} 
                  onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} 
                  placeholder="Item description" 
                />
              </div>
              <div>
                <Label>Product # / SKU (optional)</Label>
                <Input 
                  value={newItem.partNumber || ''} 
                  onChange={(e) => setNewItem({ ...newItem, partNumber: e.target.value })} 
                  placeholder="e.g., 5W30-IL-001" 
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select value={newItem.type} onValueChange={(v) => setNewItem({ ...newItem, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="labor">Labor</SelectItem>
                      <SelectItem value="parts">Parts</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Quantity</Label>
                  <Input 
                    type="number" 
                    min="1" 
                    value={newItem.defaultQuantity} 
                    onChange={(e) => setNewItem({ ...newItem, defaultQuantity: Math.max(1, parseInt(e.target.value || '1', 10)) })} 
                  />
                </div>
                <div>
                  <Label>Unit Price</Label>
                  <Input 
                    type="number" 
                    min="0" 
                    step="0.01" 
                    value={newItem.defaultUnitPrice} 
                    onChange={(e) => setNewItem({ ...newItem, defaultUnitPrice: parseFloat(e.target.value || '0') })} 
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsAddPartDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={saveItem}>
              {editingItemIndex !== null ? 'Update Item' : 'Add Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Template Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Template</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this template? It will not be added to your shop when you complete the setup.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setTemplateToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteTemplate}
            >
              Delete Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}